import { supabase } from './supabaseClient';
import { todayISO } from './dates';

// 'Low' is legacy: rows created before the redesign may still carry it, so
// it stays valid in the database and the editor keeps showing it when a task
// has it. New tasks choose from these three.
export const PRIORITIES = ['Normal', 'Medium', 'High'];

export async function listProfiles() {
  const { data, error } = await supabase.from('profiles').select('*').order('display_name');
  if (error) throw error;
  return data;
}

export async function listTodos() {
  const { data, error } = await supabase
    .from('todos')
    .select('*, assignee:profiles(id, display_name, email)')
    .order('status')
    .order('due_date', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function addTodo(todo) {
  const { data, error } = await supabase.from('todos').insert(todo).select().single();
  if (error) throw error;
  return data;
}

export async function updateTodo(id, updates) {
  const { data, error } = await supabase.from('todos').update(updates).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

export async function deleteTodo(id) {
  const { error } = await supabase.from('todos').delete().eq('id', id);
  if (error) throw error;
}

export async function setTodoStatus(id, status) {
  return updateTodo(id, {
    status,
    completed_at: status === 'Done' ? new Date().toISOString() : null,
  });
}

export async function listComments(todoId) {
  const { data, error } = await supabase
    .from('todo_comments')
    .select('*, author:profiles!todo_comments_author_id_fkey(id, display_name, email)')
    .eq('todo_id', todoId)
    .order('created_at');
  if (error) throw error;
  return data;
}

export async function addComment(todoId, authorId, body) {
  const { data, error } = await supabase
    .from('todo_comments')
    .insert({ todo_id: todoId, author_id: authorId, body })
    .select('*, author:profiles!todo_comments_author_id_fkey(id, display_name, email)')
    .single();
  if (error) throw error;
  return data;
}

export function isOverdue(todo) {
  if (todo.status === 'Done' || !todo.due_date) return false;
  const today = todayISO();
  return todo.due_date < today;
}

// ---------------------------------------------------------------------------
// Grouping by when something is due.
//
// A flat list makes you scan every row to answer "what needs doing today".
// These are pure and take `today` explicitly so they're testable without
// faking a clock — and so the comparison stays on local dates (see
// lib/dates.js for why UTC is wrong here).
// ---------------------------------------------------------------------------

export const DUE_GROUPS = [
  // Overdue is deliberately its own section rather than folded into Today.
  // Something you already missed is not the same as something due now, and
  // burying it is how it stays missed.
  { key: 'overdue', label: 'Overdue' },
  { key: 'today', label: 'Today' },
  // "Upcoming" was one undifferentiated bucket, which couldn't answer the
  // question the list exists to answer: what's coming, and how soon.
  { key: 'week', label: 'This week' },
  { key: 'later', label: 'Later' },
  { key: 'none', label: 'No due date' },
  // Undated and deliberately deprioritised. Collapsed by default so a wish
  // list doesn't compete with work that has a date on it.
  { key: 'someday', label: 'Someday', collapsed: true },
  // Finished work whose date has passed. It isn't overdue — it's done — but
  // it isn't "this week" either, and bucketing it by date alone put a task
  // completed in January under This week.
  { key: 'past', label: 'Earlier' },
];

// How far ahead "this week" reaches. Seven days rolling rather than "until
// Sunday": on a Saturday the latter would be almost empty and tell you
// nothing about the week you're actually about to work.
export const WEEK_DAYS = 7;

export function addDaysISO(dateStr, days) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d + days);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function dueGroupOf(todo, today) {
  // Undated splits two ways: parked on purpose, or simply not scheduled yet.
  if (!todo.due_date) return todo.is_someday ? 'someday' : 'none';
  // A completed task is never overdue — it's just done, and in the past.
  if (todo.due_date < today) return todo.status === 'Done' ? 'past' : 'overdue';
  if (todo.due_date === today) return 'today';
  return todo.due_date <= addDaysISO(today, WEEK_DAYS) ? 'week' : 'later';
}

export function groupTodosByDue(todos, today) {
  return DUE_GROUPS.map(({ key, label, collapsed }) => ({
    key,
    label,
    collapsed: Boolean(collapsed),
    todos: todos.filter((t) => dueGroupOf(t, today) === key),
  })).filter((section) => section.todos.length > 0);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "Today" / "Tomorrow" / "Aug 12" — a bare ISO date makes you do the
// arithmetic yourself every time you read the row.
export function formatDueLabel(dateStr, today) {
  if (!dateStr) return '';
  if (dateStr === today) return 'Today';

  const [y, m, d] = dateStr.split('-').map(Number);
  const [ty, tm, td] = today.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const tomorrow = new Date(ty, tm - 1, td + 1);
  if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';

  const label = `${MONTHS[m - 1]} ${d}`;
  return y === ty ? label : `${label} ${y}`;
}

// How many days late, for the overdue rows.
export function daysOverdue(dateStr, today) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [ty, tm, td] = today.split('-').map(Number);
  return Math.round((new Date(ty, tm - 1, td) - new Date(y, m - 1, d)) / 86400000);
}

// ---------------------------------------------------------------------------
// Inline date shorthand in the quick-add box.
//
// Typing "Make Moonflower Soaps friday" should set the due date and leave
// the title clean. Only a trailing phrase is treated as a date — a task
// called "Call supplier about Friday delivery" must not lose its subject.
// ---------------------------------------------------------------------------

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MONTH_NAMES = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

function nextWeekday(today, weekday) {
  const [y, m, d] = today.split('-').map(Number);
  const base = new Date(y, m - 1, d);
  const diff = (weekday - base.getDay() + 7) % 7;
  // Today's own weekday means next week's, not today — "friday" said on a
  // Friday is almost always the coming one.
  return addDaysISO(today, diff === 0 ? 7 : diff);
}

function monthDay(monthIndex, day, today) {
  const [ty] = today.split('-').map(Number);
  const pad = (n) => String(n).padStart(2, '0');
  const thisYear = `${ty}-${pad(monthIndex + 1)}-${pad(day)}`;
  // A date already past means next year: "15 jan" typed in December is
  // January coming, not eleven months ago.
  return thisYear < today ? `${ty + 1}-${pad(monthIndex + 1)}-${pad(day)}` : thisYear;
}

export function parseDueShorthand(input, today) {
  const text = String(input || '').trim();
  if (!text) return { title: text, dueDate: null };

  const attempts = [
    { re: /\s+today$/i, resolve: () => today },
    { re: /\s+tomorrow$/i, resolve: () => addDaysISO(today, 1) },
    { re: /\s+next\s+week$/i, resolve: () => addDaysISO(today, 7) },
    {
      re: new RegExp(`\\s+(${WEEKDAYS.join('|')})$`, 'i'),
      resolve: (match) => nextWeekday(today, WEEKDAYS.indexOf(match[1].toLowerCase())),
    },
    {
      // "15 aug" and "15 august"
      re: new RegExp(`\\s+(\\d{1,2})\\s+(${MONTH_NAMES.map((m) => m.slice(0, 3)).join('|')})[a-z]*$`, 'i'),
      resolve: (match) =>
        monthDay(MONTH_NAMES.findIndex((m) => m.startsWith(match[2].toLowerCase())), Number(match[1]), today),
    },
    {
      // "aug 15"
      re: new RegExp(`\\s+(${MONTH_NAMES.map((m) => m.slice(0, 3)).join('|')})[a-z]*\\s+(\\d{1,2})$`, 'i'),
      resolve: (match) =>
        monthDay(MONTH_NAMES.findIndex((m) => m.startsWith(match[1].toLowerCase())), Number(match[2]), today),
    },
  ];

  for (const { re, resolve } of attempts) {
    const match = text.match(re);
    if (!match) continue;
    const dueDate = resolve(match);
    // A nonsense day like "31 feb" resolves to nothing usable; leave the
    // title alone rather than silently inventing a date.
    if (!dueDate) continue;
    const title = text.replace(re, '').trim();
    // Never strip the whole title: "tomorrow" alone is the task name.
    if (!title) continue;
    return { title, dueDate };
  }

  return { title: text, dueDate: null };
}

// The activity trail. Stored on the task as jsonb, appended never rewritten,
// so a task snoozed five times shows five entries rather than one date that
// keeps moving.
export function appendActivity(todo, kind, detail) {
  const existing = Array.isArray(todo?.activity) ? todo.activity : [];
  return [...existing, { at: new Date().toISOString(), kind, detail: detail || null }];
}

/* Whether the Google Calendar event is missing, saved, or out of date.
 *
 * `added_to_calendar_at` alone can't tell "never added" from "added, then
 * the date moved" — both end up null, because rescheduling clears it. The
 * activity trail carries the difference, and it has to, since the app can
 * never ask Google what it actually holds.
 */
export function calendarState(todo) {
  if (todo?.added_to_calendar_at) return 'added';
  const activity = Array.isArray(todo?.activity) ? todo.activity : [];
  return activity.some((entry) => entry.kind === 'calendar_stale') ? 'stale' : 'none';
}
